import { Random } from "@mongez/reinforcements";

export const TABBY_MERCHANT_CODE = import.meta.env.TABBY_PROMO_MERCHANT_CODE;
export const BASE_API_URL = import.meta.env.VITE_BASE_API_URL;
export const GOOGLE_CLIENT_ID = import.meta.env.GOOGLE_CLIENT_ID;
export const BASE_OS = import.meta.env.VITE_BASE_OS;
export const __DEV__ = import.meta.env.MODE === "development";
export const TABBY_PUBLIC_KEY = import.meta.env.TABBY_PROMO_PUBLIC_KEY;
export const apiOS = import.meta.env.APP_API_OS;
export const tabbyPublicKey = import.meta.env.TABBY_PROMO_PUBLIC_KEY;
export const tabbyMerchantCode = import.meta.env.TABBY_PROMO_MERCHANT_CODE;
export const CACHE_TOKEN = import.meta.env.VITE_CACHE_TOKEN;

// Google Analytics
export const TRACK_JSON_TOKEN = import.meta.env.VITE_TRACK_JSON_TOKEN;
export const GOOGLE_TAG_MANAGER_ID = import.meta.env.VITE_GOOGLE_TAG_MANAGER_ID;
// export const NMS_GOOGLE_TAG_MANAGER_ID = import.meta.env.VITE_NMS_GOOGLE_TAG_MANAGER_ID;

export const applicationIs = (app: "wui" | "wuiTest" | "wuiTestDevelopment") =>
  apiOS === app;

// AI Chat Widget
export const AI_WIDGET_EXAMPLE_UNIT_ENDPOINT =
  import.meta.env.VITE_AI_WIDGET_EXAMPLE_UNIT_ENDPOINT;
/**
 * The loader script the storefront injects to boot the chat widget.
 *
 * Overridable so a developer can point the site at a locally built loader
 * (`http://localhost:57181/loader/dist/loader.iife.js`) and exercise the real
 * storefront integration instead of the widget's standalone page. The
 * cache-buster keeps a redeployed loader from being served stale.
 */
/**
 * Hosts where the AI chat widget integration is switched off entirely.
 *
 * The live storefront. On these hosts the widget is never injected, and the
 * `/embed/*` pages and the cart-bridge API answer 404 — so nothing of the
 * integration is reachable in production until this list says otherwise.
 * Staging hosts (beta and friends) are deliberately not listed.
 */
export const AI_WIDGET_DISABLED_HOSTS = ["zamilac.com", "www.zamilac.com"];

/** Whether the widget integration is switched off on this host name. */
export function isAiWidgetDisabledHost(hostname: string | null | undefined): boolean {
  if (!hostname) return false;

  // Drop a port and normalise case, so `ZamilAC.com:443` is caught too.
  const host = hostname.split(":")[0].toLowerCase();

  return AI_WIDGET_DISABLED_HOSTS.includes(host);
}

/**
 * Whether the widget integration is switched off for this request.
 *
 * Checks the forwarded host as well as the request URL: behind a reverse proxy
 * the URL can carry the internal address while the public one arrives in
 * `X-Forwarded-Host`, and either being the live storefront is enough.
 */
export function isAiWidgetDisabledForRequest(request: Request): boolean {
  const forwarded = request.headers.get("X-Forwarded-Host")?.split(",")[0]?.trim();

  let urlHost = "";
  try {
    urlHost = new URL(request.url).hostname;
  } catch {
    urlHost = "";
  }

  return (
    isAiWidgetDisabledHost(forwarded) ||
    isAiWidgetDisabledHost(urlHost) ||
    isAiWidgetDisabledHost(request.headers.get("Host"))
  );
}

/**
 * The widget unit this storefront talks to.
 *
 * Overridable because the production unit does not exist in a developer's
 * local backend, and the widget cannot boot without one that does.
 */
export const AI_WIDGET_UNIT_ID =
  import.meta.env.VITE_AI_WIDGET_UNIT_ID || "019f8241-e5d7-794d-b5ce-c6c9a849df30";

export const AI_WIDGET_LOADER_SRC =
  (import.meta.env.VITE_AI_WIDGET_LOADER_SRC ||
    "https://cdn.titanhub.online/ai-widget/loader.iife.js") +
  "?v=" +
  Random.string(6);

/**
 * Origin of the AI chat widget application that frames the embedded checkout.
 *
 * This is the direct parent of the `/embed/*` pages and the only origin they
 * exchange postMessages with. It is NOT the CDN the loader script is served
 * from (cdn.titanhub.online) — the loader points its iframe here.
 */
export const AI_WIDGET_APP_ORIGIN =
  import.meta.env.VITE_AI_WIDGET_APP_ORIGIN ||
  "https://ai-widget.crafted-internet.com";

/**
 * Every origin allowed to frame `/embed/*` and exchange postMessages with it.
 *
 * `VITE_AI_WIDGET_APP_ORIGIN` accepts a comma-separated list so a developer can
 * add their local widget server (`http://localhost:5173`) without editing code
 * — the embed pages are unusable from a dev machine otherwise, because
 * `frame-ancestors` rejects the frame before any of this runs.
 */
export const AI_WIDGET_APP_ORIGINS = AI_WIDGET_APP_ORIGIN.split(",")
  .map((origin: string) => origin.trim())
  .filter(Boolean);

/**
 * CSP sources for `frame-ancestors` on the embedded pages.
 *
 * `frame-ancestors` is checked against the WHOLE ancestor chain, not just the
 * immediate parent — so a developer running the widget's own demo page frames
 * store → widget → demo, and the demo's port has to be allowed too or the
 * embed is blocked outright. Those ports are handed out by whatever static
 * server the developer happened to start, so listing them is a losing game: in
 * development any local origin is accepted instead. Production keeps the
 * explicit list, unchanged.
 */
export const AI_WIDGET_FRAME_ANCESTORS = __DEV__
  ? [...AI_WIDGET_APP_ORIGINS, "http://localhost:*", "http://127.0.0.1:*"]
  : AI_WIDGET_APP_ORIGINS;

/**
 * Whether an origin may frame the embedded pages and exchange postMessages
 * with them. Mirrors {@link AI_WIDGET_FRAME_ANCESTORS} — CSP understands the
 * port wildcard, a string comparison does not.
 */
export function isAllowedWidgetOrigin(origin: string): boolean {
  if (AI_WIDGET_APP_ORIGINS.includes(origin)) return true;

  // This site's own page. When the widget runs on the store, the store's page
  // hosts these frames itself — first-party, so they carry the shopper's
  // session in browsers that block third-party cookies — and relays between
  // them and the widget. A same-origin parent is the site talking to itself.
  if (typeof window !== "undefined" && origin === window.location.origin) return true;

  return __DEV__ && /^http:\/\/(localhost|127\.0\.0\.1)(:\d+)?$/.test(origin);
}

/**
 * Where an embedded page may address a message before it knows which origin
 * framed it: the widget's origins, and this site's own page — see
 * {@link isAllowedWidgetOrigin}. Each target is still pinned, never `"*"`.
 */
export function widgetMessageOrigins(): string[] {
  if (typeof window === "undefined") return AI_WIDGET_APP_ORIGINS;

  return [...new Set([...AI_WIDGET_APP_ORIGINS, window.location.origin])];
}
